export type DayPlanActivityStatus = 'pending' | 'in_progress' | 'done' | 'blocker';

export type SkillLevel = 'skilled' | 'unskilled';
export type MaterialAvailability = 'available' | 'partial' | 'unavailable';

export interface QtyValue {
  value: number;
  unit: string;
}

export interface ManpowerLine {
  roleLabel: string;
  skillLevel: SkillLevel;
  count: number;
  sourceItemId?: string;
}

export interface MachineryLine {
  name: string;
  count: number;
  sourceItemId?: string;
}

export interface MaterialLine {
  name: string;
  qty?: number;
  unit?: string;
  availability: MaterialAvailability;
}

export interface DocumentRef {
  title: string;
  url?: string;
  /** S3 object key for uploaded files — resolve via the attachment URL endpoint. */
  fileKey?: string;
}

export interface CheckItem {
  id: string;
  label: string;
  required: boolean;
  photoRequired: boolean;
}

// === Gate types (daily gate & pre-check model) ===

export type DayType = 'W' | 'G' | 'Z' | 'M';

export type ActivityKind = 'work' | 'quality';

/** Mirrors backend IGateCompletion — enough for story stage derivation. */
export interface GateCompletionLite {
  gateId: string;
  engineerUserId: string;
  completedAt?: string;
  results?: { checkId: string; confirmed: boolean }[];
  photos?: string[];
}

export type EducationType = 'tbt' | 'method_statement' | 'induction';
export type PermitType = 'excavation' | 'electrical' | 'height' | 'lifting' | 'hotwork' | 'civil' | 'other';
/** Work-type key for Gate Packs — same closed list as PermitType. */
export type WorkTypeKey = PermitType;
export type QaqcBehavior = 'hold' | 'witness' | 'self';

export interface EducationGate {
  gateId: string;
  sourceTemplateId?: string;
  title: string;
  eduType: EducationType;
  agendaTopics: string[];
  briefing: string;
  checkItems: CheckItem[];
  attachedByManager: boolean;
}

export interface SafetyGate {
  gateId: string;
  sourceTemplateId?: string;
  title: string;
  permitType: PermitType;
  hazards: CheckItem[];
  ppe: CheckItem[];
  precautions: CheckItem[];
  revocable: boolean;
  status?: 'pending' | 'active' | 'expired' | 'revoked';
  attachedByManager: boolean;
}

export interface QaqcGate {
  gateId: string;
  sourceTemplateId?: string;
  title: string;
  behavior: QaqcBehavior;
  acceptanceCriteria: CheckItem[];
  firesBetween?: string;
  approverRoleId?: string;
  attachedByManager: boolean;
  outcomeStatus?: 'pending' | 'offered' | 'passed' | 'failed';
}

export interface ActivityGates {
  education: EducationGate[];
  qaqc: QaqcGate[];
  safety: SafetyGate[];
}

export type WorkLibraryKind = 'education' | 'safety' | 'qaqc' | 'manpower' | 'machine' | 'material';

// === Daily planning v3 — canonical closed lists (mirror backend epcDailyPlanning.constants.ts) ===

export const VARIANCE_REASONS = [
  'material_unavailable',
  'labor_unavailable',
  'equipment_breakdown',
  'prerequisite_incomplete',
  'design_rfi_pending',
  'weather',
  'inspection_permit_delay',
  'client_decision',
  'overcommitment',
  'rework',
  'other',
] as const;
export type VarianceReason = (typeof VARIANCE_REASONS)[number];

export const HINDRANCE_CATEGORIES = [
  'material_unavailable',
  'labor_unavailable',
  'equipment_breakdown',
  'prerequisite_incomplete',
  'design_rfi_pending',
  'weather',
  'inspection_permit_delay',
  'client_decision',
  'rework',
  'access_row',
  'other',
] as const;
export type HindranceCategory = (typeof HINDRANCE_CATEGORIES)[number];

export const UOM_VALUES = [
  'nos', 'm', 'm2', 'm3', 'mt', 'km', 'mwp', 'strings', 'terminations', 'sets', 'lot', 'hrs',
] as const;
export type Uom = (typeof UOM_VALUES)[number];

export type HindranceResponsibility = 'client' | 'epc' | 'subcontractor' | 'weather' | 'other';

/** Engineer-reported actual labor for the day (count 0 = planned role did not show). */
export interface ActualManpowerLine {
  roleLabel: string;
  skillLevel: SkillLevel;
  count: number;
  sourceItemId?: string;
}

export interface ActualMachineryLine {
  name: string;
  hoursWorking: number;
  sourceItemId?: string;
}

export interface ActualMaterialLine {
  name: string;
  qtyConsumed: number;
  unit: string;
}

export interface RichActivityFields {
  workLocation?: string;
  workLocationLat?: number;
  workLocationLng?: number;
  plannedQty?: QtyValue;
  actualQty?: QtyValue;
  manpower?: ManpowerLine[];
  machinery?: MachineryLine[];
  materials?: MaterialLine[];
  documents?: DocumentRef[];
  workInstructions?: string;
  dependsOnActivityIds?: string[];
  gateCompletions?: GateCompletionLite[];
  dayType?: DayType;
  gates?: ActivityGates;
  /** DM-set structured reason for a planned Z (waiting) day. Backend rejects it on non-Z day types. */
  zReasonCategory?: HindranceCategory;
  /** Activity kind: work (default) or quality check. */
  kind?: ActivityKind;
  /** Required when kind='quality' — the schedule task this inspection verifies. */
  checksTaskKey?: string;
  /** Optional — pin to one specific EpcTaskDayPlanActivity. */
  checksActivityId?: string;
  /** Optional description of exactly what is being checked. */
  checkScope?: string;
}

/** Server-owned template-generation provenance (Day-Plan Templates Phase 4).
 *  Read-only display data — the planner PUT must never send it back. */
export interface ActivityGenInfo {
  templateId: string;
  templateVersion: number;
  mappingId: string;
  generationId: string;
  gangIndex: number;
  unitIndex: number;
  stepOrder: number;
  dayInStep: number;
  slotKey: string;
  fieldsHash: string;
}

export interface DayPlanActivity extends RichActivityFields {
  id: string;
  epcProjectId: string;
  orgUnitId: string;
  taskKey: string;
  date: string; // ISO yyyy-mm-dd
  description: string;
  assignedEngineerIds: string[];
  status: DayPlanActivityStatus;
  actualHours?: number;
  remarks?: string;
  gen?: ActivityGenInfo;
  // v3 — engineer-reported actuals (never sent in the planner PUT; DM edits variance via PATCH)
  actualManpower?: ActualManpowerLine[];
  actualMachinery?: ActualMachineryLine[];
  actualMaterials?: ActualMaterialLine[];
  /** Why a W/G promise was not kept. Rejected on Z/M day types. */
  varianceReason?: VarianceReason;
  varianceNote?: string;
  createdByUserId: string;
  createdAt: string;
  updatedAt: string;
}

export interface DayPlanActivityInput extends RichActivityFields {
  id?: string; // present when updating
  date: string;
  description: string;
  assignedEngineerIds: string[];
  status?: DayPlanActivityStatus;
  actualHours?: number;
  remarks?: string;
}

export interface ReplaceDayPlanPayload {
  activities: DayPlanActivityInput[];
  deletedActivityIds: string[];
}

export interface CreateDmTaskPayload {
  taskName: string;
  wbs?: string;
  startDate: string;
  endDate: string;
  primaryEngineerId: string;
  priority?: 'low' | 'normal' | 'high';
  description?: string;
  orgUnitId: string;
}

export interface CreateDmTaskResponse {
  taskKey: string;
  snapshotId: string;
}

export interface EngineerHistoryKpis {
  completed: number;
  onTime: number;
  onTimePct: number;
  avgDurationDays: number;
  active: number;
}

export interface EngineerHistoryTaskRow {
  taskKey: string;
  taskName: string;
  startDate?: string;
  endDate?: string;
  completedOn?: string;
  durationDays?: number;
}

export interface EngineerHistoryResponse {
  engineer: { userId: string; userName: string; orgUnitId?: string; orgUnitName?: string };
  kpis: EngineerHistoryKpis;
  tasks: EngineerHistoryTaskRow[];
}

export interface AddEngineerPayload {
  userId: string;
  orgUnitId: string;
}

// === Planning tab / Engineer daily log ===

export type EngineerCardStatus = 'pending' | 'in_progress' | 'logged' | 'blocker';

export interface PlanningActivityCard {
  activityId: string;
  taskKey: string;
  description: string;
  actualHours?: number;
  status: 'pending' | 'in_progress' | 'done' | 'blocker';
  assignedEngineerIds: string[];
}

export interface PlanningEngineerCard {
  userId: string;
  userName: string;
  initials: string;
  status: EngineerCardStatus;
  activity?: { activityId: string; description: string };
  actualHours?: number;
  photoCount?: number;
  logNotePreview?: string;
  blockerText?: string;
  logSubmittedAt?: string;
  reviewed?: boolean;
}

export interface PlanningTaskGroup {
  taskKey: string;
  taskName: string;
  taskStartDate?: string;
  taskEndDate?: string;
  dayIndex?: number;
  taskDayCount?: number;
  status: 'planned' | 'needs_plan';
  engineers: PlanningEngineerCard[];
  activityCount: number;
}

export interface PlanningDaySummary {
  date: string;
  weekday: string;
  status: 'planned' | 'needs_plan' | 'empty';
  activeTaskCount: number;
  activityCount: number;
  engineerCount: number;
}

export interface PlanningFocusResponse {
  focusDate: string;
  orgUnitId: string;
  todayHeader: {
    weekday: string;
    label: string;
    summary: string;
  };
  tasks: PlanningTaskGroup[];
  nextDays: PlanningDaySummary[];
}

export interface DailyLogTimelineRow {
  date: string;
  status: EngineerCardStatus;
  summary: string;
  activityCount: number;
  hasLog: boolean;
}

export interface DailyLogPhoto {
  fileKey: string;
  caption?: string;
}

export interface DailyLogSafetyCheck {
  key: string;
  done: boolean;
  label?: string;
}

export interface DailyLogManagerNote {
  authorUserId: string;
  authorName?: string;
  text: string;
  at: string;
}

export interface DailyLogActivityRow {
  activityId: string;
  taskKey: string;
  taskName: string;
  description: string;
  actualHours?: number;
  status: 'pending' | 'in_progress' | 'done' | 'blocker';
  remarks?: string;
}

export interface DailyLogDetail {
  date: string;
  engineer: { userId: string; userName: string };
  workDone?: string;
  actualHours?: number;
  unitsDone?: string;
  weather?: { condition: 'sunny' | 'cloudy' | 'rainy' | 'hot' | 'cold'; tempC?: number };
  photos: DailyLogPhoto[];
  safetyChecks: DailyLogSafetyCheck[];
  blocker?: { text: string; reportedAt: string };
  managerNotes: DailyLogManagerNote[];
  submittedAt?: string;
  reviewedByUserId?: string;
  reviewedAt?: string;
  activities: DailyLogActivityRow[];
}

export interface PostManagerNotePayload {
  text: string;
}

// === Day-wise execution planner ===

export interface PlannerMachineLine {
  name: string;
  count: number;
}

export interface PlannerDayRollup {
  activityCount: number;
  manpowerCount: number;
  machines: PlannerMachineLine[];
  engineerIds: string[];
}

export type PlannerActivity = DayPlanActivity & { taskName: string };

export interface PlannerDay {
  date: string;
  weekday: string;
  activities: PlannerActivity[];
  rollup: PlannerDayRollup;
}

export interface PlannerUnplannedTask {
  taskKey: string;
  taskName: string;
  startDate?: string;
  endDate?: string;
}

export interface PlannerEngineerLoad {
  userId: string;
  userName: string;
  activitiesByDate: Record<string, number>;
}

export interface PlannerBoardResponse {
  from: string;
  to: string;
  orgUnitId: string;
  days: PlannerDay[];
  unplanned: PlannerUnplannedTask[];
  engineerLoad: PlannerEngineerLoad[];
}

// === Task execution history (History tab) ===

export interface TaskHistoryEngineer {
  userId: string;
  userName: string;
  logged: boolean;
  photoCount: number;
  blockerText?: string;
  logActualHours?: number;
}

export interface TaskHistoryEntry {
  activityId: string;
  date: string; // ISO yyyy-mm-dd
  description: string;
  status: DayPlanActivityStatus;
  actualHours?: number;
  remarks?: string;
  engineers: TaskHistoryEngineer[];
}

export type MyDayDprStatus = 'not_started' | 'draft' | 'submitted' | 'reviewed';

export interface MyDayResponse {
  date: string;
  dprStatus: MyDayDprStatus;
  activities: PlannerActivity[];
}

// === Daily planning v3 — quantity master, PPC, productivity, hindrances, day-seed ===

export interface ClaimingStep {
  key: string;
  label: string;
  weightPct: number;
  /** Step is blocked while a hold-behaviour QA/QC gate with this title lacks an approved hold point. */
  requiresQaqcGateTitle?: string;
}

export interface StepClaim {
  stepKey: string;
  activityId: string;
  claimedByUserId: string;
  claimedAt: string;
}

/** GET/PUT /epc/projects/:id/tasks/:taskKey/quantity — task quantity master. */
export interface TaskQuantity {
  uom: Uom;
  scopeQty: number;
  budgetedManHours?: number;
  claimingSteps?: ClaimingStep[];
  claims?: StepClaim[];
  derivedPercentComplete?: number;
  lastComputedAt?: string;
}

/** PUT body is strict — claims/derived fields are server-owned. */
export interface TaskQuantityInput {
  uom: Uom;
  scopeQty: number;
  budgetedManHours?: number;
  claimingSteps?: ClaimingStep[];
}

/** GET /epc/projects/:id/quantities — one row per task with a quantity master. */
export interface TaskQuantityRow extends TaskQuantity {
  taskKey: string;
}

/** GET /epc/projects/:id/day-plans/seed — plan-builder "Seed this day". */
export interface DaySeed {
  previousDate: string;
  previousDayIncomplete: {
    activityId: string;
    taskKey: string;
    description: string;
    status: string;
    assignedEngineerIds: string[];
  }[];
  tomorrowPlans: { engineerUserId: string; engineerName: string; text: string }[];
}

export interface PpcBucket {
  key: string; // yyyy-mm-dd | ISO week | engineer user id, per groupBy
  promised: number;
  kept: number;
  ppcPct: number;
}

export interface PpcResponse {
  buckets: PpcBucket[];
  variancePareto: { reason: string; count: number }[];
}

export interface ProductivityTaskRow {
  taskKey: string;
  uom: string;
  scopeQty: number;
  cumulativeQty: number;
  percentComplete: number;
  earnedHours: number | null;
  actualHours: number;
  manDays: number;
  unitRateActual: number | null; // qty per man-day
}

export interface ProductivityResponse {
  byTask: ProductivityTaskRow[];
  totals: { earnedHours: number; actualHours: number; productivityFactor: number | null };
}

/** List rows come back lean (`_id`); the resolve response is toJSON-serialized (`id`). */
export interface Hindrance {
  _id?: string;
  id?: string;
  epcProjectId: string;
  orgUnitId: string;
  date: string;
  taskKey?: string;
  activityId?: string;
  category: HindranceCategory;
  description: string;
  hoursLost?: number;
  responsibility: HindranceResponsibility;
  status: 'open' | 'resolved';
  photos: { fileKey: string; caption?: string }[];
  raisedByUserId: string;
  raisedVia: 'mobile' | 'web';
  resolvedByUserId?: string;
  resolvedAt?: string;
  resolutionNote?: string;
  createdAt: string;
  updatedAt: string;
}

export function hindranceId(h: Hindrance): string {
  return h._id ?? h.id ?? '';
}
